"use client";
import { useState, useEffect } from "react";
import {
    FiCalendar,
    FiClock,
    FiBookmark,
    FiShare2,
    FiHeart,
    FiUser,
    FiMessageSquare,
} from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { BlogPost } from "../../types/blogTyps/BlogPostDetail";
import { posts } from "../../data/blogData/BlogArticalsData";
import { decryptId, encryptId } from "../../components/encryption/encryption";
import BreadcrumbsComponent from "../../components/common/BreadcrumbsComponent";

const BlogPostPage = () => {
    const [post, setPost] = useState<BlogPost | null>(null);
    const [isClient, setIsClient] = useState(false);
    const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
    const [suggestedRelatedPosts, setSuggestedRelatedPosts] = useState<BlogPost[]>([]);

    const { encryptedId } = useParams<{ encryptedId?: string }>();

    let postId: number | null = null;
    if (encryptedId) {
        postId = decryptId(encryptedId);
    }

    useEffect(() => {
        setIsClient(true);
        if (postId) {
            const foundPost = posts.find((post: BlogPost) => post.id === postId);
            if (foundPost) {
                setPost(foundPost);
            }
        }
        setRelatedPosts(posts);
    }, [postId]);

    // Fixed: Filter related posts by same category (excluding current post)
    useEffect(() => {
        if (!postId || !post || relatedPosts.length === 0) return;

        const filteredPosts = relatedPosts.filter((relatedPost) => {
            return (
                relatedPost.category?.toString() === post.category?.toString() &&
                relatedPost.id !== postId
            );
        });

        setSuggestedRelatedPosts(filteredPosts.slice(0, 3)); // Limit to 3 posts
    }, [post, postId, relatedPosts]);

    // Handle conditional returns after all hooks
    if (!postId) return <div>Error: Post ID not provided</div>;

    const postDetails = posts.find((post: BlogPost) => post.id === postId);
    if (!postDetails) return <div>Post not found</div>;

    const handleLike = () => {
        if (!post) return;
        setPost({
            ...post,
            isLiked: !post.isLiked,
            likes: post.isLiked ? post.likes - 1 : post.likes + 1,
        });
    };

    const handleBookmark = () => {
        if (!post) return;
        setPost({
            ...post,
            isBookmarked: !post.isBookmarked,
        });
    };

    const handleShare = async () => {
        if (!isClient) return;
        try {
            await navigator.clipboard.writeText(window.location.href);
            alert("Link copied to clipboard!");
        } catch (err) {
            console.error("Failed to copy URL: ", err);
        }
    };

    if (!post) return <div className="min-h-screen bg-gray-50">Loading...</div>;

    const BreadCrumbsPath = [
        {
            id: 1,
            pathName: "Blogs",
            pathLink: "/pages/blogs",
        },
        {
            id: 2,
            pathName: "Blog Article",
            pathLink: `/blogs/blog-article/${postId}`,
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <BreadcrumbsComponent breadCrumbsPath={BreadCrumbsPath} />
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-7">
                <div className="flex flex-col lg:flex-row gap-12">
                    {/* Article Content */}
                    <article className="lg:w-2/3">
                        <h1 className="text-3xl md:text-3xl font-bold text-gray-900 mb-4">
                            {postDetails.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-600">
                                {postDetails.category}
                            </span>
                            {postDetails.tags.map((tag: any) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>

                        {/* Featured Image */}
                        <div className="relative xl:h-96 lg:h-96 md:h-96 sm:h-96 h-52 mb-4 rounded-md overflow-hidden">
                            <img
                                src={postDetails.blogImage}
                                alt={postDetails.title}
                                className="object-cover"
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px"
                            />
                        </div>
                        {/* Article Header */}
                        <div className="mb-8">
                            <div className="grid lg:grid-cols-4 grid-cols-2 space-x-4 text-gray-500 mb-6 text-xs">
                                <div className="flex items-center ">
                                    <FiCalendar className="mr-1.5 h-4 w-4" />
                                    <span className="">{postDetails.date}</span>
                                </div>
                                <div className="flex items-center">
                                    <FiClock className="mr-1.5 h-4 w-4" />
                                    <span>{postDetails.readTime}</span>
                                </div>
                                <div className="flex items-center">
                                    <FiMessageSquare className="mr-1.5 h-4 w-4" />
                                    <span>No Comments</span>
                                </div>
                                <div className="flex items-center">
                                    <FiUser className="mr-1.5 h-4 w-4" />
                                    <span>{postDetails.authors.author_name}</span>
                                </div>
                            </div>
                        </div>

                        {/* Article Body */}
                        <div
                            className="prose max-w-none prose-lg prose-blue prose-headings:font-semibold prose-a:text-blue-600 hover:prose-a:text-blue-800 prose-img:rounded-lg"
                            dangerouslySetInnerHTML={{ __html: postDetails.content }}
                        />

                        {/* Article Footer */}
                        <div className="mt-12 pt-6 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleLike}
                                    className={`flex items-center space-x-1 ${post.isLiked
                                        ? "text-red-500"
                                        : "text-gray-500 hover:text-red-500"
                                        }`}
                                >
                                    <FiHeart className="h-6 w-6" />
                                    <span className="font-medium">
                                        {post.likes} Likes
                                    </span>
                                </button>
                                <div className="flex space-x-4">
                                    <button
                                        onClick={handleBookmark}
                                        className={`${post.isBookmarked
                                            ? "text-yellow-500"
                                            : "text-gray-500 hover:text-yellow-500"
                                            }`}
                                    >
                                        <FiBookmark className="h-6 w-6" />
                                    </button>
                                    <button
                                        onClick={handleShare}
                                        className="text-gray-500 hover:text-blue-600"
                                    >
                                        <FiShare2 className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="lg:w-1/3">
                        <div className="bg-white p-6 rounded-lg shadow-sm sticky top-6">
                            {/* Author */}
                            <div className="flex flex-row gap-3 mb-2">
                                <div className="w-1/3">
                                    <img
                                        src={postDetails.authors.author_image}
                                        alt={postDetails.authors.author_name}
                                        height={100}
                                        width={100}
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="w-2/3">
                                    <p className="font-semibold leading-tight">
                                        Information :{" "}
                                        <span className="text-sm text-gray-600 font-normal">
                                            {postDetails.authors.author_name} {postDetails.authors.degree}
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <hr className="text-gray-300" />

                            {/* Related Posts Section */}
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Related Health Articles
                            </h3>
                            <div className="space-y-4">
                                {suggestedRelatedPosts.length > 0 ? (
                                    suggestedRelatedPosts.map((relatedPost) => (
                                        <Link
                                            key={relatedPost.id}
                                            to={`/pages/blogs/blog-article/${encryptId(relatedPost.id)}`}
                                            className="group block"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="relative h-16 w-16 flex-shrink-0 rounded-md overflow-hidden">
                                                    <img
                                                        src={relatedPost.blogImage}
                                                        alt={relatedPost.title}
                                                        className="object-cover group-hover:opacity-90 transition-opacity"
                                                        sizes="64px"
                                                    />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                                                        {relatedPost.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {relatedPost.date}
                                                    </p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        No related posts in this category
                                    </p>
                                )}
                            </div>

                            <div className="mt-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Medical Disclaimer
                                </h3>
                                <p className="text-sm text-gray-600">
                                    The content provided is for informational purposes only and
                                    should not be considered medical advice. Always consult with
                                    a qualified healthcare professional at Ceylon Hospital for
                                    personal medical concerns.
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default BlogPostPage;